import React, { useCallback, useMemo, useState } from "react";
import { DownOutlined, RedoOutlined } from "@ant-design/icons";
import { Button, Card, Dropdown, Modal, type MenuProps } from "antd";
import { useTranslation } from "react-i18next";

import { type ProgramWorkout } from "@fitness-app/data-models/entities/TrainingProgram";
import { type WorkoutTemplateWithCreator } from "@fitness-app/data-models/entities/WorkoutTemplate";
import { replaceExercisesInWorkoutFromTemplate } from "@fitness-app/utils/src/programs/replaceExercisesInWorkoutFromTemplate";
import { addFromTemplate } from "@fitness-app/utils/src/programs/workoutsTemplate";

import { PermissionKey, PermissionScope } from "@fitness-app/data-models";

import { usePermission } from "~/hooks/usePermission";
import WorkoutDay from "~/modules/TrainingPrograms/ProgramBuilder/WorkoutDay/WorkoutDay";
import ProgramDayTemplates from "~/modules/TrainingPrograms/ProgramDayTemplates/ProgramDayTemplates";
import ProgramBuilderProvider from "~/shared/providers/ProgramBuilderProvider";

const FAKE_PROGRAM_ID = "batch-add-workout";

interface BatchWorkoutDayBuilderProps {
  workout: ProgramWorkout;
  onChange: (workout: ProgramWorkout) => void;
  /** Force the per-exercise video-recording switch regardless of role (challenge video tasks need it). */
  showRecordingVideoSwitch?: boolean;
}

const BatchWorkoutDayBuilder = ({ workout, onChange, showRecordingVideoSwitch }: BatchWorkoutDayBuilderProps) => {
  const { t } = useTranslation(["workouts", "common"]);
  const canBuildTeamPrograms = usePermission(PermissionKey.PROGRAMS_MANAGE, PermissionScope.TEAM);
  const [showTemplatesModal, toggleTemplatesModal] = useState(false);
  const [selectedTemplates, setSelectedTemplates] = useState<WorkoutTemplateWithCreator[]>([]);

  const updateWorkoutInProgram = useCallback(
    ({ workout: nextWorkout }: { workout: ProgramWorkout }) => {
      onChange(nextWorkout);
    },
    [onChange],
  );

  const replaceWorkoutInProgram = useCallback(
    ({ workoutTemplate }: { workoutTemplate: WorkoutTemplateWithCreator | ProgramWorkout }) => {
      const updated = replaceExercisesInWorkoutFromTemplate(workout, workoutTemplate);
      onChange({
        ...updated,
        name: workoutTemplate.name || workout.name,
        updatedAt: new Date().toISOString(),
      });
    },
    [onChange, workout],
  );

  const noop = useCallback(async () => Promise.resolve(), []);

  const items: MenuProps["items"] = useMemo(
    () => [
      {
        key: "loadTemplate",
        icon: <RedoOutlined />,
        onClick: () => toggleTemplatesModal(true),
        label: t("workoutMenu.replaceFromTemplate"),
      },
    ],
    [t],
  );

  const onAddWorkoutFromTemplates = () => {
    const [first] = selectedTemplates;
    if (first) {
      const fromTemplate = addFromTemplate(first);
      onChange({ ...fromTemplate, id: workout.id });
    }
    toggleTemplatesModal(false);
    setSelectedTemplates([]);
  };

  return (
    <ProgramBuilderProvider
      programId={FAKE_PROGRAM_ID}
      programStatus={null}
      selectedProgram={null}
      programDetails={null}
      duplicateWorkoutInProgram={noop}
      fetchProgramWithDetails={noop}
      addNewWorkoutToProgram={noop}
      removeWorkoutFromProgram={noop}
      replaceWorkoutInProgram={replaceWorkoutInProgram}
      updateWorkoutInProgram={updateWorkoutInProgram}
      showRecordingVideoSwitch={showRecordingVideoSwitch ?? canBuildTeamPrograms}
    >
      <div>
        <Card
          className="min-h-[600px]"
          extra={
            <Dropdown menu={{ items }}>
              <Button size="small" style={{ marginBottom: 10 }}>
                Opcje <DownOutlined />
              </Button>
            </Dropdown>
          }
        >
          <WorkoutDay workout={workout} programId={FAKE_PROGRAM_ID} />
        </Card>
        <Modal
          bodyStyle={{ padding: 0 }}
          cancelText="Anuluj"
          okText="Zastąp"
          width={950}
          open={showTemplatesModal}
          onCancel={() => toggleTemplatesModal(false)}
          destroyOnClose
          okButtonProps={{ disabled: !selectedTemplates.length }}
          onOk={onAddWorkoutFromTemplates}
        >
          <ProgramDayTemplates
            selectMode
            selectionType="radio"
            onRowSelect={(picked) => setSelectedTemplates(picked)}
          />
        </Modal>
      </div>
    </ProgramBuilderProvider>
  );
};

export default BatchWorkoutDayBuilder;
